import { useState, useEffect, ChangeEvent } from 'react';
import { useNavigate, generatePath } from 'react-router';
import { InputBase, Typography, Stack } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { SearchWrapper, StyledList, StyledPagination } from './Home.styles';
import {
  MainContainer,
  ListItem,
  UserAvatar,
  InfoBlock,
  Loader,
  useNotification,
} from '../../components';
import URLs from '../URLs';
import { useSalonStore, useUserStore } from '../../store';
import { useTranslation } from 'react-i18next';
import { UserInfoMap } from '../../types';
import { filterAndRenameObjectKeys } from '../../utils';
import Cookies from 'js-cookie';

const Home = () => {
  const [searchText, setSearchText] = useState('');
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const salonsPerPage = 10;

  const { t } = useTranslation();

  const { showMessage } = useNotification();

  const { isLoading, salonList, fetchSalonList } = useSalonStore();
  const { user, getUserProfile } = useUserStore();

  useEffect(() => {
    const userId = Cookies.get('user_id');
    getUserProfile(userId, showMessage, navigate);
    fetchSalonList(userId, showMessage, navigate);
    setPage(1);
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchText]);

  const handleChange = (_: ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const filteredSalonList = searchText
    ? salonList.filter((salon) => salon.title.toLowerCase().includes(searchText.toLowerCase()))
    : salonList;
  const numberOfPages = Math.ceil(filteredSalonList.length / salonsPerPage);
  const paginatedSalonList = filteredSalonList.slice(
    (page - 1) * salonsPerPage,
    page * salonsPerPage,
  );

  return (
    <>
      <Loader isLoading={isLoading} />
      <MainContainer>
        {user && (
          <>
            <Stack gap={3} data-testid="user-name-block">
              <UserAvatar name={user?.sourceName} />
              <Typography variant="h4">{`${t('welcome')}, ${user.firstName ? user.firstName + ' ' + user.lastName : user.sourceName}`}</Typography>
            </Stack>
            {<InfoBlock infoList={filterAndRenameObjectKeys(user, UserInfoMap)} />}
          </>
        )}
        <SearchWrapper data-testid="salons-search">
          <SearchIcon />
          <InputBase
            fullWidth
            placeholder={`${t('search')}…`}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </SearchWrapper>
        <Typography variant="h6">{t('salons')}</Typography>
        <StyledList data-testid="salons-list">
          {paginatedSalonList?.map((salon) => (
            <ListItem
              key={salon.id}
              name={salon.title}
              disabled={salon.closedDate !== ''}
              action={() => navigate(generatePath(URLs.Salon, { salonId: salon.id }))}
              icon={<ChevronRightIcon />}
            />
          ))}
        </StyledList>
        <Stack mt={2} justifyContent={'center'}>
          <StyledPagination
            count={numberOfPages}
            page={page}
            onChange={handleChange}
            shape="rounded"
          />
        </Stack>
      </MainContainer>
    </>
  );
};

export default Home;
